/**
 * Delete success/failure toasts.
 *
 * Success is aggregated per batch: beginDeleteBatch records the deleted ids,
 * confirmDeleted counts the server echoes, and once all are accounted for (or a
 * settle window elapses) a single "Deleted N items" toast fires. Starting a new
 * batch supersedes the old one — the prior batch's pending echoes are dropped.
 *
 * Failure is driven by the service's id-attributed delete-failed events
 * (recordDeleteFailure), independent of the batch, so a failure still surfaces
 * even if the batch was superseded. A failed id is also released from the batch
 * so it isn't double-counted as a success.
 */

import { useErrorStore } from "./errorStore";
import { TOAST_DURATION } from "../components/toastDurations";
import { trackErrorToast } from "../services/metrics";

const SETTLE_MS = 8000;

interface DeleteBatch {
  pending: Set<string>;
  confirmed: number;
  timer: ReturnType<typeof setTimeout>;
}

let batch: DeleteBatch | null = null;

function plural(n: number): string {
  return n === 1 ? "item" : "items";
}

function finalizeSuccess(): void {
  if (!batch) return;
  clearTimeout(batch.timer);
  const { confirmed } = batch;
  batch = null;
  if (confirmed > 0) {
    useErrorStore.getState().addError(
      `Removed ${confirmed} ${plural(confirmed)}`,
      "info",
      TOAST_DURATION.SHORT,
      { title: "Deleted", hideIcon: true },
    );
  }
}

export function beginDeleteBatch(ids: string[]): void {
  if (ids.length === 0) return;
  // Supersede any in-flight batch — drop its pending echoes, no success toast.
  if (batch) clearTimeout(batch.timer);
  batch = {
    pending: new Set(ids),
    confirmed: 0,
    timer: setTimeout(finalizeSuccess, SETTLE_MS),
  };
}

export function confirmDeleted(id: string): void {
  if (!batch || !batch.pending.delete(id)) return;
  batch.confirmed += 1;
  if (batch.pending.size === 0) finalizeSuccess();
}

/** A server-reported delete failure for a specific id. Surfaces immediately and
 *  releases the id from the current batch (so it isn't counted as a success). */
export function recordDeleteFailure(id: string, error: string): void {
  batch?.pending.delete(id);
  trackErrorToast("Gallery", "delete_failed");
  useErrorStore.getState().addError(error, "error", TOAST_DURATION.SHORT);
}
